import { useState } from 'react';
import Head from 'next/head';
import { Layout } from '../../components';

export default function ZeroManualReports() {
	const [properties, setProperties] = useState(5);
	const [hoursPerReport, setHoursPerReport] = useState(3);
	const [hourlyRate, setHourlyRate] = useState(50);

	const hoursSaved = properties * hoursPerReport * 12;
	const daysSaved = hoursSaved / 8;
	const costSaved = hoursSaved * hourlyRate;

	const formatNumber = (val) =>
		val.toLocaleString('en-US', { maximumFractionDigits: 1 });

	return (
		<Layout pageName="Zero Manual Reports">
			<Head>
				<title>Zero Manual Reports | FacilityBot</title>
				<meta
					name="description"
					content="Replace monthly PowerPoint decks and Excel exports with live FM dashboards. Build reports in seconds by typing a prompt into FacilityBot AI Studio."
				/>
				<link rel="canonical" href="https://facilitybot.co/resources/zero-manual-reports" />
				<meta property="og:type" content="website" />
				<meta
					property="og:title"
					content="Say Goodbye to Monthly PowerPoint Decks &amp; Manual Excel Reports"
				/>
				<meta
					property="og:description"
					content="Live FM dashboards and natural-language reporting with FacilityBot AI Studio."
				/>
				<meta property="og:url" content="https://facilitybot.co/resources/zero-manual-reports" />
			</Head>

			<div className="zmr">
				<header className="zmr-hero">
					<div className="zmr-wrap zmr-hero-grid">
						<div className="zmr-hero-copy">
							<span className="zmr-eyebrow">Zero Manual Reports</span>
							<h1>Say Goodbye to Monthly PowerPoint Decks &amp; Manual Excel Reports.</h1>
							<p className="zmr-lead">
								Shift to continuous, live FM intelligence. Build dashboards and custom reports in
								seconds using natural language with FacilityBot AI Studio.
							</p>
							<div className="zmr-hero-actions">
								<a
									className="zmr-btn zmr-btn-primary"
									href="https://calendly.com/facilitybot/facilitybot-demo"
									target="_blank"
									rel="noopener noreferrer"
								>
									Schedule a Demo
								</a>
								<a className="zmr-btn zmr-btn-outline" href="#videos">
									Watch AI Studio in Action
								</a>
							</div>
						</div>

						<figure
							className="zmr-mock"
							role="img"
							aria-label="Illustration: a typed prompt in FacilityBot AI Studio produces a bar chart of the top fault categories."
						>
							<div className="zmr-mock-prompt" aria-hidden="true">
								<svg
									width="20"
									height="20"
									viewBox="0 0 24 24"
									fill="none"
									stroke="#F05A28"
									strokeWidth="2"
									strokeLinecap="round"
									strokeLinejoin="round"
								>
									<path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9L12 3z" />
									<path d="M19 16l.8 2.2L22 19l-2.2.8L19 22l-.8-2.2L16 19l2.2-.8L19 16z" />
								</svg>
								<span>Show top 5 fault categories this quarter</span>
								<span className="zmr-caret" />
							</div>
							<div className="zmr-mock-card" aria-hidden="true">
								<div className="zmr-mock-title">Top fault categories</div>
								<div className="zmr-mock-row">
									<span>Air-con</span>
									<span className="zmr-mock-track">
										<span className="zmr-mock-fill" style={{ '--w': '92%' }} />
									</span>
								</div>
								<div className="zmr-mock-row">
									<span>Plumbing</span>
									<span className="zmr-mock-track">
										<span className="zmr-mock-fill" style={{ '--w': '71%' }} />
									</span>
								</div>
								<div className="zmr-mock-row">
									<span>Electrical</span>
									<span className="zmr-mock-track">
										<span className="zmr-mock-fill" style={{ '--w': '54%' }} />
									</span>
								</div>
								<div className="zmr-mock-row">
									<span>Cleaning</span>
									<span className="zmr-mock-track">
										<span className="zmr-mock-fill" style={{ '--w': '38%' }} />
									</span>
								</div>
								<div className="zmr-mock-row">
									<span>Lifts</span>
									<span className="zmr-mock-track">
										<span className="zmr-mock-fill" style={{ '--w': '22%' }} />
									</span>
								</div>
							</div>
							<figcaption className="zmr-mock-note" aria-hidden="true">
								Illustration only
							</figcaption>
						</figure>
					</div>
				</header>

				<main>
					{/* Pain vs gain */}
					<section className="zmr-section" aria-labelledby="zmr-why">
						<div className="zmr-wrap">
							<h2 id="zmr-why">Why Zero Manual Reports?</h2>
							<p className="zmr-sec-intro">
								Monthly reporting keeps your best people in spreadsheets instead of on site. Live
								reporting gives them the time back and gives your stakeholders answers today, not
								next month.
							</p>

							<div className="zmr-compare">
								<article className="zmr-card old" aria-labelledby="zmr-old">
									<span className="zmr-tag">The old way</span>
									<h3 id="zmr-old">Copy, paste, format, send</h3>
									<ul className="zmr-list">
										<li>Your team spends 15+ hours a month extracting CSVs.</li>
										<li>
											Analysts format static PowerPoint slides for every client and management
											review.
										</li>
										<li>The data goes stale the second the PDF lands in an inbox.</li>
										<li>Custom client questions wait for the next monthly cycle.</li>
									</ul>
								</article>

								<article className="zmr-card new" aria-labelledby="zmr-new">
									<span className="zmr-tag">The FacilityBot way</span>
									<h3 id="zmr-new">Live, on demand, always current</h3>
									<ul className="zmr-list">
										<li>Live, interactive dashboards stay open to your team and clients 24/7.</li>
										<li>
											Type a prompt into AI Studio and it builds or updates the dashboard instantly.
										</li>
										<li>Auto-scheduled executive digests reach management on your timetable.</li>
										<li>One source of truth covers every operation and IoT sensor.</li>
									</ul>
								</article>
							</div>
						</div>
					</section>

					{/* Pillars */}
					<section className="zmr-section alt" aria-labelledby="zmr-features">
						<div className="zmr-wrap">
							<h2 id="zmr-features">Reporting That Builds Itself</h2>
							<p className="zmr-sec-intro">Three capabilities replace the monthly reporting cycle.</p>

							<div className="zmr-pillars">
								<article className="zmr-card zmr-pillar" aria-labelledby="zmr-p1">
									<div className="zmr-icon" aria-hidden="true">
										<svg
											width="28"
											height="28"
											viewBox="0 0 24 24"
											fill="none"
											stroke="#F05A28"
											strokeWidth="1.8"
											strokeLinecap="round"
											strokeLinejoin="round"
										>
											<path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1.1-4.6A8 8 0 1 1 21 12z" />
											<path d="M12 8.5l.9 2.1 2.1.9-2.1.9-.9 2.1-.9-2.1-2.1-.9 2.1-.9.9-2.1z" />
										</svg>
									</div>
									<span className="zmr-badge">FacilityBot AI Studio</span>
									<h3 id="zmr-p1">Natural Language Reporting</h3>
									<p>Type what you want to see. Studio generates or updates the visual report for you.</p>
									<ul className="zmr-chips">
										<li>&ldquo;Add a month filter for IoT sensor activity&rdquo;</li>
										<li>&ldquo;Show top 5 fault categories this quarter&rdquo;</li>
									</ul>
								</article>

								<article className="zmr-card zmr-pillar" aria-labelledby="zmr-p2">
									<div className="zmr-icon" aria-hidden="true">
										<svg
											width="28"
											height="28"
											viewBox="0 0 24 24"
											fill="none"
											stroke="#F05A28"
											strokeWidth="1.8"
											strokeLinecap="round"
											strokeLinejoin="round"
										>
											<rect x="3" y="3" width="7" height="9" rx="1.5" />
											<rect x="14" y="3" width="7" height="5" rx="1.5" />
											<rect x="14" y="12" width="7" height="9" rx="1.5" />
											<rect x="3" y="16" width="7" height="5" rx="1.5" />
										</svg>
									</div>
									<h3 id="zmr-p2">Custom &amp; Interactive Dashboards</h3>
									<p>
										Bring fault tickets, preventive maintenance and live IoT (internet-connected) sensor
										readings into one view across multiple sites.
									</p>
									<ul className="zmr-chips">
										<li>People counters</li>
										<li>Indoor air quality</li>
										<li>Ammonia levels</li>
									</ul>
								</article>

								<article className="zmr-card zmr-pillar" aria-labelledby="zmr-p3">
									<div className="zmr-icon" aria-hidden="true">
										<svg
											width="28"
											height="28"
											viewBox="0 0 24 24"
											fill="none"
											stroke="#F05A28"
											strokeWidth="1.8"
											strokeLinecap="round"
											strokeLinejoin="round"
										>
											<rect x="3" y="5" width="18" height="14" rx="2" />
											<path d="M3 7l9 6 9-6" />
										</svg>
									</div>
									<h3 id="zmr-p3">Automated Executive Digests</h3>
									<p>
										Schedule email summaries to management or clients. FacilityBot sends them on time,
										with no manual PDF exports.
									</p>
								</article>
							</div>
						</div>
					</section>

					{/* Case-study videos */}
					<section className="zmr-section" id="videos" aria-labelledby="zmr-video-h">
						<div className="zmr-wrap">
							<h2 id="zmr-video-h">See AI Studio in Action</h2>
							<p className="zmr-sec-intro">
								Two real deployments in education. Each one changed its dashboard by typing a sentence.
							</p>

							<div className="zmr-videos">
								<article className="zmr-video-card">
									<div className="zmr-embed">
										<iframe
											src="https://www.youtube-nocookie.com/embed/1NCfBSyYwPQ"
											title="FacilityBot Studio: Joint Inspection (JI) &amp; Sensor Filtering Dashboard"
											loading="lazy"
											referrerPolicy="strict-origin-when-cross-origin"
											allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
											allowFullScreen
										/>
									</div>
									<div className="zmr-video-body">
										<span className="zmr-badge">Polytechnic &middot; IoT dashboard</span>
										<h3>FacilityBot Studio: Joint Inspection (JI) &amp; Sensor Filtering Dashboard</h3>
										<p>
											See how a Polytechnic site updated its IoT sensor dashboard by typing a natural
											language prompt. The prompt added monthly filters across all charts, with no
											manual reconfiguration.
										</p>
									</div>
								</article>

								<article className="zmr-video-card">
									<div className="zmr-embed">
										<iframe
											src="https://www.youtube-nocookie.com/embed/SEkgZzeBDb0"
											title="FacilityBot Studio: School Sensor Dashboards"
											loading="lazy"
											referrerPolicy="strict-origin-when-cross-origin"
											allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
											allowFullScreen
										/>
									</div>
									<div className="zmr-video-body">
										<span className="zmr-badge">School &middot; Sensor dashboards</span>
										<h3>FacilityBot Studio: School Sensor Dashboards</h3>
										<p>
											Learn how the FM team at a Singapore School centralises live people counter and
											ammonia sensor readings across different sites. They build and refine operational
											views in minutes with AI Studio prompts.
										</p>
									</div>
								</article>
							</div>
						</div>
					</section>

					{/* Calculator */}
					<section className="zmr-section alt" aria-labelledby="zmr-calc-h">
						<div className="zmr-wrap">
							<h2 id="zmr-calc-h">Calculate the Time You Get Back</h2>
							<p className="zmr-sec-intro">
								Enter how many properties you report on and how long each report takes. The calculator
								shows the hours and cost you spend on manual reporting every year.
							</p>

							<div className="zmr-calc" id="zmr-calc">
								<div className="zmr-card">
									<div className="zmr-fields">
										<div className="zmr-field">
											<label htmlFor="zmr-props">Properties you manage</label>
											<div className="zmr-field-row">
												<input
													type="range"
													id="zmr-props-range"
													min="1"
													max="200"
													value={properties}
													aria-label="Properties you manage, slider"
													onChange={(e) => setProperties(Number(e.target.value))}
												/>
												<input
													type="number"
													id="zmr-props"
													min="1"
													max="200"
													step="1"
													value={properties}
													inputMode="numeric"
													onChange={(e) => {
														const val = parseFloat(e.target.value);
														setProperties(isNaN(val) ? 1 : Math.min(200, Math.max(1, val)));
													}}
												/>
											</div>
										</div>

										<div className="zmr-field">
											<label htmlFor="zmr-hours">Hours spent per report, per month</label>
											<div className="zmr-field-row">
												<input
													type="range"
													id="zmr-hours-range"
													min="1"
													max="40"
													value={hoursPerReport}
													aria-label="Hours per report per month, slider"
													onChange={(e) => setHoursPerReport(Number(e.target.value))}
												/>
												<input
													type="number"
													id="zmr-hours"
													min="1"
													max="40"
													step="1"
													value={hoursPerReport}
													inputMode="numeric"
													onChange={(e) => {
														const val = parseFloat(e.target.value);
														setHoursPerReport(isNaN(val) ? 1 : Math.min(40, Math.max(1, val)));
													}}
												/>
											</div>
										</div>

										<div className="zmr-field">
											<label htmlFor="zmr-rate">Blended hourly cost ($)</label>
											<div className="zmr-field-row">
												<input
													type="range"
													id="zmr-rate-range"
													min="10"
													max="200"
													step="5"
													value={hourlyRate}
													aria-label="Blended hourly cost in dollars, slider"
													onChange={(e) => setHourlyRate(Number(e.target.value))}
												/>
												<input
													type="number"
													id="zmr-rate"
													min="10"
													max="200"
													step="1"
													value={hourlyRate}
													inputMode="numeric"
													onChange={(e) => {
														const val = parseFloat(e.target.value);
														setHourlyRate(isNaN(val) ? 10 : Math.min(200, Math.max(10, val)));
													}}
												/>
											</div>
											<p className="zmr-hint">Defaults to $50. Change it to match your team&apos;s real cost.</p>
										</div>
									</div>
								</div>

								<div className="zmr-results" aria-live="polite" aria-atomic="true">
									<div className="zmr-result">
										<strong id="zmr-out-hours">{formatNumber(hoursSaved)}</strong>
										<span>hours saved per year</span>
									</div>
									<div className="zmr-result sub">
										<strong id="zmr-out-days">{formatNumber(daysSaved)}</strong>
										<span>working days back</span>
									</div>
									<div className="zmr-result">
										<strong id="zmr-out-cost">${formatNumber(costSaved)}</strong>
										<span>operational cost saved per year</span>
									</div>
								</div>
							</div>

							<p className="zmr-foot">
								Estimate = properties &times; hours per report &times; 12 months. It assumes reporting
								time drops to near zero once dashboards go live. A working day is 8 hours.
							</p>
							<p className="zmr-calc-cta">
								<a
									className="zmr-btn zmr-btn-primary"
									href="https://calendly.com/facilitybot/facilitybot-demo"
									target="_blank"
									rel="noopener noreferrer"
								>
									Schedule a Demo
								</a>
							</p>
						</div>
					</section>
				</main>
			</div>
		</Layout>
	);
}
